import { isSortable } from '@dnd-kit/react/sortable';

import type { TDeal } from '@crm/entities/deal';
import type { TBucketKey, TDealColumn } from '@crm/entities/deals-board';
import type { DragEndEvent, DragOverEvent } from '@dnd-kit/react';

type TDealLocation = {
    columnIndex: number;
    dealIndex: number;
};

type TDndEntityData = {
    group?: string | number;
    columnId?: string | number;
    dealId?: string | number;
    bucketKey?: TBucketKey;
};

type TDndEntity = DragOverEvent['operation']['source'] | DragOverEvent['operation']['target'];

const getDndEntityData = (entity: TDndEntity) => entity?.data as TDndEntityData | undefined;

const getEntityDataValue = (entity: TDndEntity, key: keyof TDndEntityData) => {
    const value = getDndEntityData(entity)?.[key];

    return value !== undefined ? String(value) : undefined;
};

export const getColumnIndexById = (columns: TDealColumn[], columnId: string) =>
    columns.findIndex((column) => String(column.id) === columnId);

export const getDealLocation = (columns: TDealColumn[], dealId: string): TDealLocation | null => {
    const columnIndex = columns.findIndex((column) =>
        column.deals.some((deal) => String(deal.id) === dealId)
    );

    if (columnIndex === -1) {
        return null;
    }

    const dealIndex = columns[columnIndex].deals.findIndex((deal) => String(deal.id) === dealId);

    if (dealIndex === -1) {
        return null;
    }

    return { columnIndex, dealIndex };
};

export const getColumnDealIds = (column: TDealColumn) => [
    ...new Set(column.deals.map((deal) => deal.id)),
];

export const getColumnIds = (columns: TDealColumn[]) => [
    ...new Set(columns.map((column) => column.id)),
];

export const getTargetColumnId = (target: DragOverEvent['operation']['target']) => {
    if (!target) {
        return undefined;
    }

    const targetColumnId = getEntityDataValue(target, 'columnId');

    if (targetColumnId !== undefined) {
        return targetColumnId;
    }

    if (isSortable(target)) {
        return String(target.group);
    }

    return getEntityDataValue(target, 'group');
};

export const getTargetDealId = (target: DragOverEvent['operation']['target']) => {
    if (!target || !isSortable(target) || target.type !== 'deal') {
        return undefined;
    }

    return getEntityDataValue(target, 'dealId') ?? String(target.id);
};

export const getTargetTerminalBucketKey = (target: DragOverEvent['operation']['target']) =>
    getDndEntityData(target)?.bucketKey;

export const getTargetBoardColumnId = (target: DragOverEvent['operation']['target']) => {
    if (!target || !isSortable(target) || target.type !== 'deal-column') {
        return undefined;
    }

    return getEntityDataValue(target, 'columnId') ?? String(target.id);
};

export const getSourceDealId = (source: DragOverEvent['operation']['source']) => {
    if (!source || source.type !== 'deal') {
        return undefined;
    }

    return getEntityDataValue(source, 'dealId') ?? String(source.id);
};

export const getSourceBoardColumnId = (source: DragOverEvent['operation']['source']) => {
    if (!source || source.type !== 'deal-column') {
        return undefined;
    }

    return getEntityDataValue(source, 'columnId') ?? String(source.id);
};

export const getTerminalBucketDropResult = (
    event: DragEndEvent
): { dealId: TDeal['id']; bucketKey: TBucketKey } | undefined => {
    if (event.canceled) {
        return undefined;
    }

    const sourceDealId = getSourceDealId(event.operation.source);
    const bucketKey = getTargetTerminalBucketKey(event.operation.target);

    if (!sourceDealId || !bucketKey) {
        return undefined;
    }

    const dealId = Number(sourceDealId);

    if (!Number.isFinite(dealId)) {
        return undefined;
    }

    return { dealId, bucketKey };
};

export const moveColumn = (
    columns: TDealColumn[],
    sourceColumnId: string,
    targetColumnId: string
) => {
    const sourceColumnIndex = getColumnIndexById(columns, sourceColumnId);
    const targetColumnIndex = getColumnIndexById(columns, targetColumnId);

    if (
        sourceColumnIndex === -1 ||
        targetColumnIndex === -1 ||
        sourceColumnIndex === targetColumnIndex ||
        columns[sourceColumnIndex].isDefault ||
        columns[targetColumnIndex].isDefault
    ) {
        return columns;
    }

    const nextColumns = [...columns];
    const [column] = nextColumns.splice(sourceColumnIndex, 1);

    nextColumns.splice(targetColumnIndex, 0, column);

    return nextColumns;
};

export const moveDeal = (
    columns: TDealColumn[],
    dealId: string,
    targetColumnId: string,
    targetDealId?: string
) => {
    const sourceLocation = getDealLocation(columns, dealId);
    const targetColumnIndex = getColumnIndexById(columns, targetColumnId);

    if (!sourceLocation || targetColumnIndex === -1) {
        return columns;
    }

    const targetDealIndex =
        targetDealId !== undefined
            ? columns[targetColumnIndex].deals.findIndex((deal) => String(deal.id) === targetDealId)
            : columns[targetColumnIndex].deals.length;

    if (targetDealId !== undefined && targetDealIndex === -1) {
        return columns;
    }

    if (
        sourceLocation.columnIndex === targetColumnIndex &&
        sourceLocation.dealIndex === targetDealIndex
    ) {
        return columns;
    }

    const normalizedTargetDealIndex =
        sourceLocation.columnIndex === targetColumnIndex &&
        sourceLocation.dealIndex < targetDealIndex
            ? targetDealIndex - 1
            : targetDealIndex;

    const nextColumns = [...columns];

    if (sourceLocation.columnIndex === targetColumnIndex) {
        const sourceColumn = columns[sourceLocation.columnIndex];
        const deals = [...sourceColumn.deals];
        const [deal] = deals.splice(sourceLocation.dealIndex, 1);

        deals.splice(normalizedTargetDealIndex, 0, deal);
        nextColumns[sourceLocation.columnIndex] = { ...sourceColumn, deals };

        return nextColumns;
    }

    const sourceColumn = columns[sourceLocation.columnIndex];
    const targetColumn = columns[targetColumnIndex];
    const sourceDeals = [...sourceColumn.deals];
    const targetDeals = [...targetColumn.deals];
    const [deal] = sourceDeals.splice(sourceLocation.dealIndex, 1);

    targetDeals.splice(normalizedTargetDealIndex, 0, deal);
    nextColumns[sourceLocation.columnIndex] = { ...sourceColumn, deals: sourceDeals };
    nextColumns[targetColumnIndex] = { ...targetColumn, deals: targetDeals };

    return nextColumns;
};
